import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import ProtectedRoutes from "./routes/ProtectedRoutes";
import PublicRoutes from "./routes/PublicRoutes";
import RepRoutes from "./routes/RepRoutes";

import Client from "./components/pages/Client";
import ClientRefresh from "./components/pages/ClientRefresh";
import ClientExports from "./components/pages/ClientExports";
import ClientImports from "./components/pages/ClientImports";
import ClientImportExport from "./components/pages/ClientImportExport";
import Clients from "./components/pages/Clients";
import Communication from "./components/pages/Communication";
import WorkQueueMonitor from "./components/pages/WorkQueueMonitor";
import CreateMessage from "./components/pages/CreateMessage";
import EditMessage from "./components/pages/EditMessage";
import Documents from "./components/pages/Documents";
import DocumentationManagement from "./components/pages/DocumentationManagement";
import DocumentationCenter from "./components/pages/DocumentationCenter";
import ErrorLogs from "./components/pages/ErrorLogs";
import ExpensesDashboard from "./components/pages/ExpensesDashboard";
import CallDurationOutliers from "./components/pages/CallDurationOutliers";
import CallList from "./components/pages/CallList";
import ArtifactBuilder from "./components/pages/ArtifactBuilder";
import Exports from "./components/pages/Exports";
import Home from "./components/pages/Home";
import Imports from "./components/pages/Imports";
import Inventory from "./components/pages/Inventory";
import InventoryClientSelector from "./components/pages/InventoryClientSelector";
import InvoicingPage from "./components/pages/InvoicingPage";
import Locations from "./components/pages/Locations";
import Login from "./components/pages/Login";
import MessageInbox from "./components/pages/MessageInbox";
import Messages from "./components/pages/Messages";
import MessageThread from "./components/pages/MessageThread";
import Mobile from "./components/pages/Mobile";
import PayPeriodProcessing from "./components/pages/PayPeriodProcessing";
import Payroll from "./components/pages/Payroll";
import PayReceipt from "./components/pages/PayReceipt";
import AdpConfiguration from "./components/pages/AdpConfiguration";
import ExpenseAccounts from "./components/pages/ExpenseAccounts";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PublicLinks from "./pages/PublicLinks";
import Support from "./pages/Support";
import Project from "./components/pages/Project";
import ProjectRefresh from "./components/pages/ProjectRefresh";
import PublicLinksAdmin from "./components/pages/PublicLinksAdmin";
import ProjectImportExport from "./components/pages/ProjectImportExport";
import ProjectSchedule from "./components/pages/ProjectSchedule";
import BulkCallEditor from "./components/pages/BulkCallEditor";
import Retailer from "./components/pages/Retailer";
import Retailers from "./components/pages/Retailers";
import Banner from "./components/pages/Banner";
import Location from "./components/pages/Location";
import SelectedLocations from "./components/pages/SelectedLocations";
import SupportTickets from "./components/pages/SupportTickets";
import Survey from "./components/pages/Survey";
import SurveyAnalysis from "./components/pages/SurveyAnalysis";
import SurveyAnswerExport from "./components/pages/SurveyAnswerExport";
import SurveyDesigner from "./components/pages/SurveyDesigner";
import SurveyMetrics from "./components/pages/SurveyMetrics";
import SurveyImages from "./components/pages/SurveyImages";
import OrdersConfigHome from "./components/pages/OrdersConfigHome";
import OrdersClassificationPage from "./components/pages/OrdersClassificationPage";
import OrdersPolicyPage from "./components/pages/OrdersPolicyPage";
import OrdersTransmissionProfilesPage from "./components/pages/OrdersTransmissionProfilesPage";
import OrdersTransmissionRoutesPage from "./components/pages/OrdersTransmissionRoutesPage";
import OrdersMonitoringHome from "./components/pages/OrdersMonitoringHome";
import OrdersSubmissionsPage from "./components/pages/OrdersSubmissionsPage";
import OrdersTransmissionsPage from "./components/pages/OrdersTransmissionsPage";
import OrdersReceiptsPage from "./components/pages/OrdersReceiptsPage";
import OrdersViewExportPage from "./components/pages/OrdersViewExportPage";
import SyncDashboard from "./components/pages/SyncDashboard";
import SyncReport from "./components/pages/SyncReport";
import SyncSupport from "./components/pages/SyncSupport";
import OrderDashboard from "./components/dashboard/OrderDashboard";
import ClientDashboard from "./components/pages/ClientDashboard";
import ClientUserDashboard from "./components/pages/ClientUserDashboard";
import ClientPortalModules from "./components/pages/ClientPortalModules";
import ClientPortalCatalog from "./components/pages/ClientPortalCatalog";
import RepDashboard from "./components/pages/RepDashboard";
import Synchronization from "./components/pages/Synchronization";
import Syndication from "./components/pages/Syndication";
import SyndicationImportExport from "./components/pages/SyndicationImportExport";
import Territory from "./components/pages/Territory";
import User from "./components/pages/User";
import UserExports from "./components/pages/UserExports";
import Users from "./components/pages/Users";
import SupervisorCalendarPage from "./components/pages/SupervisorCalendar";
import QAPage from "./components/pages/QAPage";
import ScheduledLocations from "./components/pages/ScheduledLocations";
import RepHome from "./components/pages/Dashboard";

import Toast from "./components/ui/Toast";
import GlobalErrorHandler from "./components/ui/GlobalErrorHandler";
import AuthInterceptorProvider from "./components/providers/AuthInterceptorProvider";

function App() {
  const role = useSelector((state) => state.authentication.role);
  const isRoleAdmin = role === "admin";
  const isRep = role === "rep" && !isRoleAdmin;

  return (
    <>
      <BrowserRouter>
        <AuthInterceptorProvider>
          <Routes>
            <Route element={<PublicRoutes />}>
              <Route
                path="/login"
                element={<Login />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPolicy />}
              />
              <Route
                path="/support"
                element={<Support />}
              />
              <Route
                path="/links"
                element={<PublicLinks />}
              />
            </Route>

            <Route element={<ProtectedRoutes />}>
              <Route
                path="/clients/"
                element={<Clients />}
              ></Route>

              <Route
                path="/client/"
                element={<Client />}
              ></Route>

              <Route
                path="/clientrefresh"
                element={
                  role === "sysadmin" ? (
                    <ClientRefresh />
                  ) : (
                    <Navigate to="/home" />
                  )
                }
              ></Route>

              <Route
                path="/clientexports/"
                element={<ClientExports />}
              ></Route>

              <Route
                path="/clientimports/"
                element={<ClientImports />}
              ></Route>

              <Route
                path="/clientimportexport/"
                element={<ClientImportExport />}
              ></Route>

              <Route
                path="/communication"
                element={<Communication />}
              ></Route>

              <Route
                path="/workqueue-monitor"
                element={<WorkQueueMonitor />}
              ></Route>

              <Route
                path="/documents/"
                element={<Documents />}
              ></Route>

              <Route
                path="/documentation"
                element={<DocumentationManagement />}
              ></Route>

              <Route
                path="/documentation-center"
                element={<DocumentationCenter />}
              ></Route>

              <Route
                path="/errorlogs"
                element={<ErrorLogs />}
              ></Route>

              <Route
                path="/exports"
                element={<Exports />}
              ></Route>

              <Route
                path="/home"
                element={isRep ? <Navigate to="/rep-home" /> : <Home />}
              ></Route>

              <Route
                path="/supervisor-calendar"
                element={<SupervisorCalendarPage />}
              ></Route>

              <Route
                path="/qa"
                element={isRep ? <Navigate to="/rep-home" /> : <QAPage />}
              ></Route>

              <Route element={<RepRoutes />}>
                <Route
                  path="/rep-home"
                  element={<RepHome />}
                ></Route>
                <Route
                  path="/scheduled-locations"
                  element={<ScheduledLocations />}
                ></Route>
                <Route
                  path="/pay-receipt"
                  element={<PayReceipt />}
                ></Route>
              </Route>

              <Route
                path="/imports"
                element={<Imports />}
              ></Route>

              <Route
                path="/inventory"
                element={<InventoryClientSelector />}
              ></Route>

              <Route
                path="/inventory/:projectId"
                element={<Inventory />}
              ></Route>

              <Route
                path="/invoicing"
                element={<InvoicingPage />}
              ></Route>

              <Route
                path="/locations"
                element={<Locations />}
              ></Route>

              <Route
                path="/messages"
                element={<Messages />}
              ></Route>

              <Route
                path="/createmessage"
                element={<CreateMessage />}
              ></Route>

              <Route
                path="/editmessage/:messageId"
                element={<EditMessage />}
              ></Route>

              <Route
                path="/messageinbox"
                element={<MessageInbox />}
              ></Route>

              <Route
                path="/messagethread/:messageId"
                element={<MessageThread />}
              ></Route>

              <Route
                path="/mobile"
                element={<Mobile />}
              />

              <Route
                path="/links"
                element={<PublicLinks />}
              />

              <Route
                path="/links-admin"
                element={<PublicLinksAdmin />}
              />

              <Route
                path="/payperiodprocessing"
                element={<PayPeriodProcessing />}
              ></Route>

              <Route
                path="/payroll"
                element={<Payroll />}
              ></Route>

              <Route
                path="/finance/adp-configuration"
                element={<AdpConfiguration />}
              ></Route>

              <Route
                path="/finance/expense-accounts"
                element={<ExpenseAccounts />}
              ></Route>

              <Route
                path="/project"
                element={<Project />}
              ></Route>

              <Route
                path="/projectrefresh"
                element={
                  role === "sysadmin" ? (
                    <ProjectRefresh />
                  ) : (
                    <Navigate to="/home" />
                  )
                }
              ></Route>

              <Route
                path="/calllist"
                element={<CallList />}
              ></Route>

              <Route
                path="/artifactbuilder"
                element={<ArtifactBuilder />}
              ></Route>

              <Route
                path="/projectimportexport"
                element={<ProjectImportExport />}
              ></Route>

              <Route
                path="/projectschedule"
                element={<ProjectSchedule />}
              ></Route>

              <Route
                path="/bulkcalleditor"
                element={<BulkCallEditor />}
              ></Route>

              <Route
                path="/retailers"
                element={<Retailers />}
              ></Route>

              <Route
                path="/retailer"
                element={<Retailer />}
              ></Route>

              <Route
                path="/banner"
                element={<Banner />}
              ></Route>

              <Route
                path="/location"
                element={<Location />}
              ></Route>

              <Route
                path="/selectedlocations"
                element={<SelectedLocations />}
              ></Route>

              <Route
                path="/survey"
                element={<Survey />}
              ></Route>

              <Route
                path="/surveyimages"
                element={<SurveyImages />}
              ></Route>

              <Route
                path="/surveyresults"
                element={<SurveyAnalysis />}
              ></Route>

              <Route
                path="/survey-answer-export"
                element={<SurveyAnswerExport />}
              ></Route>

              <Route
                path="/surveydesigner"
                element={<SurveyDesigner />}
              ></Route>

              <Route
                path="/surveymetrics"
                element={<SurveyMetrics />}
              ></Route>

              <Route
                path="/orderdashboard"
                element={<OrderDashboard />}
              ></Route>

              <Route
                path="/orders/config"
                element={<OrdersConfigHome />}
              ></Route>

              <Route
                path="/clientorders"
                element={<OrdersConfigHome />}
              ></Route>

              <Route
                path="/orders/config/classifications"
                element={<OrdersClassificationPage />}
              ></Route>

              <Route
                path="/clientorders/classifications"
                element={<OrdersClassificationPage />}
              ></Route>

              <Route
                path="/orders/config/policies"
                element={<OrdersPolicyPage />}
              ></Route>

              <Route
                path="/clientorders/policies"
                element={<OrdersPolicyPage />}
              ></Route>

              <Route
                path="/orders/config/transmission-profiles"
                element={<OrdersTransmissionProfilesPage />}
              ></Route>

              <Route
                path="/clientorders/transmission-profiles"
                element={<OrdersTransmissionProfilesPage />}
              ></Route>

              <Route
                path="/orders/config/transmission-routes"
                element={<OrdersTransmissionRoutesPage />}
              ></Route>

              <Route
                path="/clientorders/transmission-routes"
                element={<OrdersTransmissionRoutesPage />}
              ></Route>

              <Route
                path="/clientorders/orders"
                element={<OrdersViewExportPage />}
              ></Route>

              <Route
                path="/orders/monitor"
                element={<OrdersMonitoringHome />}
              ></Route>

              <Route
                path="/orders/monitor/submissions"
                element={<OrdersSubmissionsPage />}
              ></Route>

              <Route
                path="/orders/monitor/transmissions"
                element={
                  <Navigate
                    to="/orders/monitor"
                    replace
                  />
                }
              ></Route>

              <Route
                path="/orders/monitor/receipts"
                element={<OrdersReceiptsPage />}
              ></Route>

              <Route
                path="/clientdashboard"
                element={<ClientDashboard />}
              ></Route>

              <Route
                path="/client-portal"
                element={
                  role === "client" ? (
                    <ClientUserDashboard />
                  ) : (
                    <Navigate to="/home" />
                  )
                }
              ></Route>

              <Route
                path="/client-portal-modules"
                element={
                  ["admin", "hr", "sysadmin", "finance"].includes(role) ? (
                    <ClientPortalModules />
                  ) : (
                    <Navigate to="/home" />
                  )
                }
              ></Route>

              <Route
                path="/client-portal-catalog"
                element={
                  ["admin", "hr", "sysadmin", "finance"].includes(role) ? (
                    <ClientPortalCatalog />
                  ) : (
                    <Navigate to="/home" />
                  )
                }
              ></Route>

              <Route
                path="/repdashboard"
                element={<RepDashboard />}
              ></Route>

              <Route
                path="/expenses"
                element={
                  isRep ? <Navigate to="/rep-home" /> : <ExpensesDashboard />
                }
              ></Route>

              <Route
                path="/call-duration-outliers"
                element={
                  isRep ? <Navigate to="/rep-home" /> : <CallDurationOutliers />
                }
              ></Route>

              <Route
                path="/syncdashboard"
                element={<SyncDashboard />}
              ></Route>

              <Route
                path="/syncreport"
                element={<SyncReport />}
              ></Route>

              <Route
                path="/synchronization"
                element={<Synchronization />}
              ></Route>

              <Route
                path="/sync-support"
                element={<SyncSupport />}
              ></Route>

              <Route
                path="/supporttickets"
                element={<SupportTickets />}
              ></Route>

              <Route
                path="/syndication"
                element={<Syndication />}
              ></Route>

              <Route
                path="/syndicationimportexport"
                element={<SyndicationImportExport />}
              ></Route>

              <Route
                path="/territory"
                element={<Territory />}
              ></Route>

              <Route
                path="/user"
                element={<User />}
              ></Route>

              <Route
                path="/users"
                element={<Users />}
              ></Route>

              <Route
                path="/userexports"
                element={<UserExports />}
              ></Route>

              <Route
                path="/*"
                element={isRep ? <Navigate to="/rep-home" /> : <Home />}
              ></Route>
            </Route>
          </Routes>
        </AuthInterceptorProvider>
      </BrowserRouter>

      <Toast />
      <GlobalErrorHandler />
    </>
  );
}

export default App;
